import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';
import { api, handleApiError } from '../lib/api';
import { startWebSocket } from '../utils/websocket';

type AuthErrors = Record<string, string>;

const Login = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<AuthErrors>({});
  const [successMessage, setSuccessMessage] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  const validateForm = () => {
    const newErrors: AuthErrors = {};

    // Email validation
    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Email is invalid';
    }

    // Password validation
    if (!password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getRedirectPath = (user: { role: string }) => {
    return user.role === 'ADMIN' ? '/admin' : '/feed';
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setIsLoading(true);
    setErrors({});
    setSuccessMessage('');

    if (!validateForm()) {
      setIsLoading(false);
      return;
    }

    try {
      const { data } = await api.auth.login({
        email,
        password,
        rememberMe,
      });

      // Store user data and token for websocket auth
      if (data.data?.user) {
        localStorage.setItem('user', JSON.stringify(data.data.user));
      }
      if (data.data?.token) {
        localStorage.setItem('token', data.data.token);
      }

      startWebSocket();

      setSuccessMessage('Login successful! Redirecting...');

      // Frontend routing
      const redirectPath = getRedirectPath(data.data.user);
      setTimeout(() => {
        navigate(redirectPath, { replace: true });
      }, 1000);

    } catch (error: any) {
      console.error('Login error:', error);
      if (error.data?.errors && Array.isArray(error.data.errors)) {
        const errorMap: AuthErrors = {};
        error.data.errors.forEach((err: any) => {
          errorMap[err.path || 'general'] = err.msg || err.message;
        });
        setErrors(errorMap);
      } else {
        const message = handleApiError(error);
        setErrors({ general: message });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    navigate('/reset-password');
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const renderError = (field: string) => {
    if (errors[field]) {
      return (
        <div className="flex items-center space-x-1 mt-1">
          <AlertCircle className="w-4 h-4 text-red-400" />
          <span className="text-sm text-red-400">{errors[field]}</span>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, #ffffff 1px, transparent 1px),
                           radial-gradient(circle at 75% 75%, #ffffff 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          {/* Hero Section */}
          <div className="text-center mb-8">
            <img
              src="/wellsite-logo-white.svg"
              alt="Wellsite logo"
              className="mx-auto h-12 mb-6"
            />
            {successMessage && (
              <div className="flex items-center justify-center bg-green-900/50 border border-green-700 rounded-lg p-3 mb-4">
                <span className="text-green-400">{successMessage}</span>
              </div>
            )}
            {errors.general && (
              <div className="flex items-center justify-center space-x-2 bg-red-900/50 border border-red-700 rounded-lg p-3 mb-4">
                <AlertCircle className="w-5 h-5 text-red-400" />
                <span className="text-red-400">{errors.general}</span>
              </div>
            )}
          </div>

          {/* Login Form */}
          <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-700 rounded-2xl p-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`w-full px-4 py-3 bg-black border rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white focus:border-white transition-colors ${
                    errors.email ? 'border-red-500' : 'border-gray-600'
                  }`}
                  placeholder=""
                  required
                />
                {renderError('email')}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Password *
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={`w-full px-4 py-3 pr-12 bg-black border rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white focus:border-white transition-colors ${
                      errors.password ? 'border-red-500' : 'border-gray-600'
                    }`}
                    required
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-700 rounded transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5 text-gray-400" />
                    ) : (
                      <Eye className="w-5 h-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {renderError('password')}
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-4 h-4 bg-black border border-gray-600 rounded focus:ring-2 focus:ring-white"
                  />
                  <span className="ml-2 text-sm text-gray-300">Remember me</span>
                </label>
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  className="text-sm text-white hover:text-gray-300 transition-colors"
                >
                  Reset password
                </button>
              </div>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={isLoading}
                className={`w-full py-3 rounded-lg font-medium transition-all duration-200 ${
                  isLoading
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-black hover:bg-gray-200 hover:shadow-lg transform hover:scale-[1.02]'
                }`}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                    <span>Logging In...</span>
                  </div>
                ) : (
                  'Login'
                )}
              </button>
            </div>
            <div className="mt-6 text-center">
              <p className="text-gray-400">
                Not a customer?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/register')}
                  className="text-white font-medium hover:underline transition-colors"
                >
                  Sign up
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="relative z-10 p-6 text-center">
        <p className="text-gray-500 text-sm">
          © 2025 Wellsite. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default Login;
